import React, { useState, useRef, useEffect } from 'react'
import { colors } from '../../constants/colors'
import { intentAPI } from '../../services/(adminapi)/intentAPI'
import NotificationModal from '../NotificationModal'
import { resolveMediaPublicUrl } from '../../utils/mediaUrl'

const MAX_FILE_SIZE_MB = 10
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024
const ALLOWED_EXT = /\.(jpe?g|png|svg|zip)$/i

const CreateFlowModal = ({
  isOpen,
  onClose,
  onSave,
  isEditMode = false,
  existingFlow = null,
  isSaving = false,
}) => {
  const [formData, setFormData] = useState({
    title: '',
    linkedIntents: '',
    description: ''
  })
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [dragOver, setDragOver] = useState(false)
  const [availableIntents, setAvailableIntents] = useState([])
  const [loadingIntents, setLoadingIntents] = useState(false)
  const fileInputRef = useRef(null)
  
  // Notification modal state
  const [notification, setNotification] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: ''
  })

  // Fetch available intents when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchAvailableIntents()
    }
  }, [isOpen])

  // Populate form with existing flow data when in edit mode
  useEffect(() => {
    if (isOpen && isEditMode && existingFlow) {
      setFormData({
        title: existingFlow.title || '',
        linkedIntents: existingFlow.intent_id || existingFlow.intent_key || '',
        description: existingFlow.description || ''
      })
      // Set existing files if any
      if (existingFlow.steps && existingFlow.steps.length > 0) {
        const existingFiles = existingFlow.steps.map((step, index) => ({
          id: `existing-${index}`,
          name: step.title || `Step ${index + 1}`,
          type: step.media_asset_id?.mime_type || 'image',
          url: step.media_asset_id ? resolveMediaPublicUrl(step.media_asset_id._id) : '',
          isExisting: true,
          media_asset_id: step.media_asset_id?._id || step.media_asset_id || null,
          description: step.description || ''
        }))
        setUploadedFiles(existingFiles)
      }
    } else if (isOpen && !isEditMode) {
      // Reset form for create mode
      setFormData({
        title: '',
        linkedIntents: '',
        description: ''
      })
      setUploadedFiles([])
    }
  }, [isOpen, isEditMode, existingFlow])

  const fetchAvailableIntents = async () => {
    try {
      setLoadingIntents(true)
      const response = await intentAPI.getIntents()
      
      if (response.data.success) {
        setAvailableIntents(response.data.data)
      } else {
        console.error('Failed to fetch intents:', response.data.error)
        setAvailableIntents([])
      }
    } catch (error) {
      console.error('Error fetching intents:', error)
      setAvailableIntents([])
    } finally {
      setLoadingIntents(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleFileSelect = (files) => {
    if (isSaving) return
    const fileArray = Array.from(files || [])
    const accepted = []
    const rejected = []

    fileArray.forEach((file) => {
      if (!ALLOWED_EXT.test(file.name)) {
        rejected.push(`${file.name}: unsupported type (use .jpg, .png, .svg, or .zip)`)
        return
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        const sizeMb = (file.size / (1024 * 1024)).toFixed(1)
        rejected.push(`${file.name}: ${sizeMb} MB — max is ${MAX_FILE_SIZE_MB} MB`)
        return
      }
      accepted.push({
        id: `${Date.now()}-${Math.random()}`,
        name: file.name,
        size: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
        status: 'ready',
        file,
      })
    })

    if (rejected.length > 0) {
      showNotification(
        'warning',
        'Image too large or invalid',
        rejected.join(' · ')
      )
    }
    if (accepted.length > 0) {
      setUploadedFiles((prev) => [...prev, ...accepted])
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    if (!isSaving) setDragOver(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setDragOver(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    if (isSaving) return
    handleFileSelect(e.dataTransfer.files)
  }

  const removeFile = (fileId) => {
    if (isSaving) return
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId))
  }

  const showNotification = (type, title, message) => {
    setNotification({
      isOpen: true,
      type,
      title,
      message
    })
  }

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isOpen: false }))
  }

  const handleSave = async () => {
    if (isSaving) return

    if (!formData.title.trim()) {
      showNotification('warning', 'Validation Error', 'Please enter a title')
      return
    }

    if (!formData.linkedIntents) {
      showNotification('warning', 'Validation Error', 'Please select an intent')
      return
    }

    if (uploadedFiles.length === 0 && !isEditMode) {
      showNotification('warning', 'Validation Error', 'Add at least one image for the visual guide steps.')
      return
    }

    const oversized = uploadedFiles.find(
      (f) => f.file instanceof File && f.file.size > MAX_FILE_SIZE_BYTES
    )
    if (oversized) {
      showNotification(
        'warning',
        'Image too large',
        `"${oversized.name}" exceeds ${MAX_FILE_SIZE_MB} MB. Choose a smaller image.`
      )
      return
    }

    const saveData = {
      ...formData,
      intentKey: formData.linkedIntents,
      files: uploadedFiles,
    }

    if (isEditMode && existingFlow) {
      saveData.existingFlow = existingFlow
    }

    // Parent owns closing the modal after upload finishes; keep form until then
    await onSave(saveData)
  }

  const handleRequestClose = () => {
    if (isSaving) return
    onClose()
  }

  const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: isOpen ? 'flex' : 'none',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  }

  const modalStyle = {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '32px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
  }

  const titleStyle = {
    fontSize: '24px',
    fontWeight: '700',
    color: colors.text.primary,
    marginBottom: '24px'
  }

  const formGroupStyle = {
    marginBottom: '20px'
  }

  const labelStyle = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: '8px'
  }

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    border: '1px solid #D1D5DB',
    borderRadius: '8px',
    fontSize: '14px',
    color: colors.text.primary,
    backgroundColor: 'white',
    boxSizing: 'border-box'
  }

  const textareaStyle = {
    ...inputStyle,
    minHeight: '100px',
    resize: 'vertical'
  }

  const dropdownStyle = {
    ...inputStyle,
    backgroundImage: 'url("data:image/svg+xml,%3csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3e%3cpath stroke=\'%236b7280\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'m6 8 4 4 4-4\'/%3e%3c/svg%3e")',
    backgroundPosition: 'right 12px center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '16px',
    paddingRight: '40px'
  }

  const uploadAreaStyle = {
    border: `2px dashed ${dragOver ? colors.primary : colors.primary}`,
    borderRadius: '8px',
    padding: '40px 20px',
    textAlign: 'center',
    backgroundColor: dragOver ? colors.primary + '10' : '#FAFAFA',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginBottom: '16px'
  }

  const uploadIconStyle = {
    width: '48px',
    height: '48px',
    color: '#9CA3AF',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px auto'
  }

  const uploadTextStyle = {
    fontSize: '16px',
    color: colors.text.primary,
    marginBottom: '16px'
  }

  const browseButtonStyle = {
    backgroundColor: colors.primary,
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  }

  const fileNoteStyle = {
    fontSize: '12px',
    color: '#6B7280',
    textAlign: 'center'
  }

  const fileCardStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: '12px',
    backgroundColor: '#F9FAFB',
    borderRadius: '8px',
    marginBottom: '8px',
    border: '1px solid #E5E7EB'
  }

  const fileIconStyle = {
    width: '24px',
    height: '24px',
    marginRight: '12px',
    color: '#6B7280'
  }

  const fileInfoStyle = {
    flex: 1
  }

  const fileNameStyle = {
    fontSize: '14px',
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: '2px'
  }

  const fileSizeStyle = {
    fontSize: '12px',
    color: '#6B7280'
  }

  const actionButtonStyle = {
    padding: '12px 24px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    border: 'none',
    marginLeft: '12px'
  }

  const cancelButtonStyle = {
    ...actionButtonStyle,
    backgroundColor: 'white',
    color: colors.text.primary,
    border: '1px solid #D1D5DB'
  }

  const saveButtonStyle = {
    ...actionButtonStyle,
    backgroundColor: colors.primary,
    color: 'white',
    opacity: isSaving ? 0.7 : 1,
    cursor: isSaving ? 'not-allowed' : 'pointer',
  }

  const buttonContainerStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '32px',
    paddingTop: '24px',
    borderTop: '1px solid #E5E7EB'
  }

  if (!isOpen) return null

  return (
    <div style={modalOverlayStyle} onClick={handleRequestClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h2 style={titleStyle}>{isEditMode ? 'Edit Flow' : 'Create New Flow'}</h2>

        {isSaving && (
          <div
            style={{
              marginBottom: 16,
              padding: '10px 12px',
              borderRadius: 8,
              backgroundColor: '#EFF6FF',
              border: '1px solid #BFDBFE',
              color: '#1D4ED8',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            Uploading… Please wait. Large images can take a minute.
          </div>
        )}
        
        <div style={formGroupStyle}>
          <label style={labelStyle}>Title</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="Login Help Flow"
            style={inputStyle}
            disabled={isSaving}
          />
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Linked Intent(s)</label>
          <select
            name="linkedIntents"
            value={formData.linkedIntents}
            onChange={handleInputChange}
            style={dropdownStyle}
            disabled={loadingIntents || isSaving}
          >
            <option value="">Select an intent...</option>
            {availableIntents.map((intent) => (
              <option key={intent._id} value={intent._id}>
                {intent.name}
              </option>
            ))}
          </select>
          {loadingIntents && (
            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
              Loading intents...
            </div>
          )}
          {!loadingIntents && availableIntents.length === 0 && (
            <div style={{ fontSize: '12px', color: '#EF4444', marginTop: '4px' }}>
              No intents available. Create an intent first.
            </div>
          )}
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Enter flow description..."
            style={textareaStyle}
            disabled={isSaving}
          />
        </div>

        <div style={formGroupStyle}>
          <div
            style={{
              ...uploadAreaStyle,
              opacity: isSaving ? 0.6 : 1,
              pointerEvents: isSaving ? 'none' : 'auto',
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !isSaving && fileInputRef.current?.click()}
          >
            <div style={uploadIconStyle}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
              </svg>
            </div>
            <div style={uploadTextStyle}>Drag and Drop Files here or</div>
            <button
              type="button"
              style={browseButtonStyle}
              disabled={isSaving}
              onClick={(e) => {
                e.stopPropagation()
                if (!isSaving) fileInputRef.current?.click()
              }}
            >
              Browse Files
            </button>
            <div style={fileNoteStyle}>
              .jpg, .png, .svg, .zip — max {MAX_FILE_SIZE_MB} MB per file
            </div>
          </div>
          
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".jpg,.jpeg,.png,.svg,.zip"
            style={{ display: 'none' }}
            disabled={isSaving}
            onChange={(e) => {
              handleFileSelect(e.target.files)
              e.target.value = ''
            }}
          />
        </div>

        {/* Selected files (uploaded on Save) */}
        {uploadedFiles.map((file) => (
          <div key={file.id} style={fileCardStyle}>
            <div style={fileIconStyle}>📄</div>
            <div style={fileInfoStyle}>
              <div style={fileNameStyle}>{file.name}</div>
              <div style={fileSizeStyle}>{file.size || (file.isExisting ? 'Existing media' : '')}</div>
            </div>
            <button
              style={{
                background: 'none',
                border: 'none',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                color: '#EF4444',
                opacity: isSaving ? 0.5 : 1,
              }}
              disabled={isSaving}
              onClick={() => removeFile(file.id)}
            >
              ✕
            </button>
          </div>
        ))}

        <div style={buttonContainerStyle}>
          <button
            style={{
              ...cancelButtonStyle,
              opacity: isSaving ? 0.6 : 1,
              cursor: isSaving ? 'not-allowed' : 'pointer',
            }}
            onClick={handleRequestClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button style={saveButtonStyle} onClick={handleSave} disabled={isSaving}>
            {isSaving
              ? 'Uploading…'
              : isEditMode
                ? 'Update Flow'
                : 'Save'}
          </button>
        </div>
      </div>

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.isOpen}
        onClose={closeNotification}
        type={notification.type}
        title={notification.title}
        message={notification.message}
      />
    </div>
  )
}

export default CreateFlowModal
